import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle, Circle, Loader2, RefreshCw, Banknote, Send, Copy,
  CalendarClock, Landmark, Mail, Plus, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FlagRow = ({ ok, label, testId }) => (
  <div className={`flex items-center gap-2 text-sm ${ok ? "text-emerald-700" : "text-slate-500"}`} data-testid={testId}>
    {ok ? <CheckCircle className="w-4 h-4 shrink-0" /> : <Circle className="w-4 h-4 shrink-0 text-slate-300" />}
    {label}
  </div>
);

const SUB_STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800",
  pending_customer_approval: "bg-amber-100 text-amber-800",
  cancelled: "bg-slate-100 text-slate-500",
  finished: "bg-blue-100 text-blue-700",
};

// AUS Stage 3 — repayments collect via GoCardless BECS Direct Debit / PayTo.
// GC-synced: finds the deal's billing request + mandate + subscriptions live.
export const BecsMandatePanel = ({ application, onRefresh, actor = "portal", isFunded = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const appId = application?.application_id || application?.id;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/applications/${appId}/becs-status`);
      setStatus(data);
    } catch (e) {
      console.error("BECS status load failed", e);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  const handleConfirm = async () => {
    if (!status?.mandate_authorised &&
        !window.confirm("GoCardless hasn't reported the mandate as authorised yet. Confirm the Direct Debit as active anyway?")) {
      return;
    }
    try {
      setConfirming(true);
      await api.post(`/applications/${appId}/becs-confirm?confirmed_by=${encodeURIComponent(actor)}`);
      toast.success("Direct Debit (BECS) mandate confirmed active");
      await load();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm the mandate");
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelSub = async (s) => {
    if (!window.confirm(`Cancel this subscription in GoCardless?\n\n${s.name || s.id}\nA$${(Number(s.amount || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} — no further collections will be taken.`)) return;
    try {
      await api.post(`/applications/${appId}/becs-subscriptions/${s.id}/cancel?cancelled_by=${encodeURIComponent(actor)}`);
      toast.success("Subscription cancelled in GoCardless");
      await load();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const confirmed = application?.standing_order_initiated || status?.confirmed;
  const br = status?.billing_request;
  const subs = status?.subscriptions || [];
  const coverage = status?.cycle_coverage;
  const cycleOk = coverage ? coverage.ok : true;
  const activeSubs = subs.filter((s) => ["active", "pending_customer_approval"].includes(s.status));
  const weekdayOf = (s) => (s.start_date ? new Date(s.start_date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short" }) : "?");
  const dupWeekdays = [...new Set(
    activeSubs.map(weekdayOf).filter((d, _i, arr) => arr.filter((x) => x === d).length > 1)
  )];

  return (
    <div className="space-y-4" data-testid="becs-mandate-panel">
      {confirmed ? (
        <div className="p-4 bg-emerald-50 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <p className="font-medium text-emerald-800">Direct Debit (GC BECS) Mandate Active</p>
          </div>
          <p className="text-sm text-emerald-700">
            Confirmed by {status?.confirmed_by || application?.becs_mandate_confirmed_by || "portal"}
            {(status?.confirmed_at || application?.becs_mandate_confirmed_at) &&
              ` on ${new Date(status?.confirmed_at || application?.becs_mandate_confirmed_at).toLocaleString("en-AU")}`}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="becs-intro">
          Repayments are collected by GoCardless Direct Debit (BECS) or PayTo. The status below is synced live from GoCardless.
        </p>
      )}

      {/* GC live sync — billing request / mandate / subscriptions */}
      <div className="p-3 border rounded-lg bg-white space-y-2" data-testid="becs-gc-sync">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">GoCardless — live status</p>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
        {loading && !status ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-1">
            <Loader2 className="w-4 h-4 animate-spin" /> Searching GoCardless…
          </div>
        ) : (
          <>
            <FlagRow ok={status?.mandate_sms_sent} label="Mandate invite sent to customer" testId="becs-flag-sms" />
            <FlagRow ok={status?.mandate_created} label={br?.billing_request_id
              ? `Billing request in GoCardless (${br.billing_request_id} • ${String(br.scheme || "").includes("pay") ? "PayTo" : "BECS"} • ${br.status})`
              : "Billing request created in GoCardless"} testId="becs-flag-created" />
            <FlagRow ok={status?.mandate_authorised} label={status?.mandate_id
              ? `Mandate authorised by customer (${status.mandate_id}${status?.mandate_status ? ` • ${status.mandate_status.replace(/_/g, " ")}` : ""})`
              : "Mandate authorised by customer"} testId="becs-flag-authorised" />

            {subs.length > 0 ? (
              <div className="mt-2 border-t pt-2 space-y-1" data-testid="becs-subscriptions-list">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <CalendarClock className="w-3.5 h-3.5" /> Payment cycle ({subs.length} subscription{subs.length > 1 ? "s" : ""})
                </p>
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-xs gap-2" data-testid={`becs-sub-${s.id}`}>
                    <span className="truncate text-slate-700">{s.name || s.id}</span>
                    <span className="flex items-center gap-2 shrink-0 font-mono">
                      A${(Number(s.amount || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      <span className="text-slate-400">×{s.count || "∞"}</span>
                      <Badge className={`${SUB_STATUS_STYLE[s.status] || "bg-slate-100 text-slate-600"} text-[10px] px-1.5`}>{s.status}</Badge>
                      {["active", "pending_customer_approval"].includes(s.status) && (
                        <button
                          type="button"
                          onClick={() => handleCancelSub(s)}
                          className="text-slate-300 hover:text-red-600 transition-colors"
                          title="Cancel this subscription in GoCardless"
                          data-testid={`becs-cancel-sub-${s.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  </div>
                ))}
                {dupWeekdays.length > 0 && (
                  <div className="mt-1 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="becs-dup-warning">
                    ⚠ {dupWeekdays.join(", ")} has more than one active subscription — the customer would be charged twice
                    that day. Cancel the accidental duplicate in the GoCardless dashboard.
                  </div>
                )}
              </div>
            ) : status?.mandate_id ? (
              <p className="text-xs text-amber-700 mt-1" data-testid="becs-no-subs-note">
                Mandate is authorised but no payment cycle exists yet — set it up below.
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setWizardOpen(true)}
          disabled={isFunded || !status}
          title={!status ? "Waiting for the GoCardless status and the instalment from LoanDisk…" : undefined}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="becs-setup-dd-btn"
        >
          {!status ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Landmark className="w-4 h-4 mr-2" />}
          {status?.mandate_id ? "Direct Debit Wizard" : "Set Up Direct Debit"}
        </Button>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="becs-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        {!confirmed && (
          <Button
            onClick={handleConfirm}
            disabled={confirming || isFunded || !cycleOk}
            title={!cycleOk ? (coverage?.required === "daily"
              ? `Daily cycle incomplete — missing: ${(coverage?.missing_weekdays || []).join(", ")}`
              : "Create the payment cycle first") : undefined}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="becs-confirm-btn"
          >
            {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Banknote className="w-4 h-4 mr-2" />}
            Confirm Direct Debit Active
          </Button>
        )}
      </div>
      {!confirmed && !loading && coverage && !cycleOk && (
        <p className="text-xs text-amber-700" data-testid="becs-coverage-note">
          {coverage.required === "daily"
            ? `Daily plan — an active subscription is needed on every weekday before the Direct Debit can be confirmed. Missing: ${(coverage.missing_weekdays || []).join(", ")}.`
            : "Weekly plan — at least one active subscription is needed before the Direct Debit can be confirmed."}
        </p>
      )}

      {wizardOpen && (
        <DDSetupDialog
          appId={appId}
          application={application}
          status={status}
          onClose={() => setWizardOpen(false)}
          onChanged={async () => { await load(); if (onRefresh) onRefresh(); }}
          actor={actor}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DD Wizard — creates the GC mandate invite (same infra as the Onboarding
// Centre wizard, GC bit only) and then the payment cycle on the mandate.
// ---------------------------------------------------------------------------
function DDSetupDialog({ appId, application, status, onClose, onChanged, actor }) {
  const invite = status?.invite_prefill || {};
  const plan = status?.plan_prefill || {};
  const sfa = status?.sfa_number || appId;

  // Collections policy: PayTo is the standard for WEEKLY plans (~80% of AU
  // banks support it); Direct Debit for DAILY plans or when the bank refuses.
  const policyScheme = (plan.frequency || "weekly") === "weekly" ? "pay_to" : "becs";
  const [scheme, setScheme] = useState(policyScheme);
  const [givenName, setGivenName] = useState(invite.given_name || "");
  const [familyName, setFamilyName] = useState(invite.family_name || "");
  const [email, setEmail] = useState(invite.email || "");
  const [mobile, setMobile] = useState(invite.mobile || "");
  const [creating, setCreating] = useState(false);
  const [inviteResult, setInviteResult] = useState(
    status?.billing_request?.authorisation_url
      ? { billing_request_id: status.billing_request.billing_request_id, authorisation_url: status.billing_request.authorisation_url }
      : null
  );
  const [sendingEmail, setSendingEmail] = useState(false);

  // Payment cycle form — when subs already exist, match them (they define the
  // real cycle); otherwise prefill from the contract offer
  const activeExisting = (status?.subscriptions || []).filter((s) => ["active", "pending_customer_approval"].includes(s.status));
  const existingAmt = activeExisting.length ? Number(activeExisting[0].amount) / 100 : null;
  const existingCount = activeExisting.length ? activeExisting[0].count : null;
  const [amount, setAmount] = useState(existingAmt ?? plan.amount_per_payment ?? "");
  const [frequency, setFrequency] = useState(plan.frequency || "weekly");
  const [weeks, setWeeks] = useState(existingCount ?? plan.count ?? "");
  const [startDate, setStartDate] = useState(plan.start_date || "");
  const [creatingSubs, setCreatingSubs] = useState(false);
  const [subsResult, setSubsResult] = useState(null);

  const mandateId = status?.mandate_id;
  const authUrl = inviteResult?.authorisation_url;

  const handleCreateInvite = async () => {
    try {
      setCreating(true);
      const { data } = await api.post("/aus/gocardless/billing-request", {
        scheme,
        customer_name: `${givenName} ${familyName}`.trim(),
        business_name: invite.business_name || application?.company_name || "",
        sfa_number: sfa,
        customer_email: email || undefined,
        customer_given_name: givenName || undefined,
        customer_family_name: familyName || undefined,
        bank_account_holder_name: invite.bank?.account_name || undefined,
        bank_branch_code: invite.bank?.bsb || undefined,
        bank_account_number: invite.bank?.account_number || undefined,
        payment_amount: scheme !== "becs" && amount ? Number(amount) : undefined,
        payment_frequency: scheme !== "becs" ? (frequency || "weekly") : undefined,
      });
      setInviteResult(data);
      toast.success(`Mandate invite created in GoCardless${data.bank_prefill === "prefilled" ? " (bank details prefilled)" : ""}`);
      onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (scheme !== "becs" && /payto|pay_to|currency_doesnt_support/i.test(String(detail))) {
        setScheme("becs");
        toast.warning("PayTo isn't available for this customer — switched to BECS Direct Debit (policy fallback). Click Create again.");
      } else {
        toast.error(detail || "Failed to create the mandate invite");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleReissueAsDD = async () => {
    if (!window.confirm("Re-issue this invite as BECS Direct Debit? The PayTo one stays in GoCardless but is superseded.")) return;
    try {
      setCreating(true);
      const { data } = await api.post("/aus/gocardless/billing-request", {
        scheme: "becs",
        customer_name: `${givenName} ${familyName}`.trim(),
        business_name: invite.business_name || application?.company_name || "",
        sfa_number: sfa,
        customer_email: email || undefined,
        customer_given_name: givenName || undefined,
        customer_family_name: familyName || undefined,
        bank_account_holder_name: invite.bank?.account_name || undefined,
        bank_branch_code: invite.bank?.bsb || undefined,
        bank_account_number: invite.bank?.account_number || undefined,
      });
      setScheme("becs");
      setInviteResult(data);
      toast.success("Re-issued as BECS Direct Debit — send the new authorisation link");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to re-issue as Direct Debit");
    } finally {
      setCreating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!email) { toast.error("Enter the customer's email first"); return; }
    try {
      setSendingEmail(true);
      await api.post("/aus/gocardless/send-mandate-email", {
        customer_email: email,
        customer_name: `${givenName} ${familyName}`.trim() || invite.customer_name || "Customer",
        business_name: invite.business_name || application?.company_name || "",
        authorisation_url: authUrl,
        scheme,
        sfa_number: sfa,
      });
      toast.success(`Authorisation email sent to ${email}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email send failed");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateCycle = async () => {
    try {
      setCreatingSubs(true);
      const { data } = await api.post(`/applications/${appId}/becs-subscriptions`, {
        amount_per_payment: Number(amount),
        frequency,
        count: Number(weeks),
        start_date: startDate,
        confirmed_by: actor,
      });
      setSubsResult(data);
      if (data.status === "success") {
        toast.success(`Payment cycle created — ${data.created.length} subscription(s) in GoCardless`);
      } else if (data.status === "partial") {
        toast.warning(`${data.created.length} created, ${data.errors.length} failed — see details below`);
      }
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment cycle creation failed");
    } finally {
      setCreatingSubs(false);
    }
  };

  const numPayments = frequency === "daily" ? Number(weeks || 0) * 5 : Number(weeks || 0);
  // PayTo agreement limits (Dean, 23 Oct 2026): what we'd set on a new agreement, and what the live one actually allows
  const capPreview = useMemo(() => {
    const base = plan.payto_cap_preview || null;
    const a = Number(amount);
    if (!base || !(a > 0) || a === Number(plan.amount_per_payment)) return base;
    const mult = Number(base.multiplier) || 2;
    const cap = Math.ceil(a * mult * 100) / 100;
    return { ...base, instalment: a, max_amount_per_payment: cap, explain: `${mult}× the A$${a.toFixed(2)} ${frequency || ""} instalment you entered → up to A$${cap.toFixed(2)} per payment, up to ${base.max_payments_per_week} payments a week` };
  }, [plan.payto_cap_preview, plan.amount_per_payment, amount, frequency]);
  const liveLimits = status?.payto_limits || null;
  const overCap = !!(liveLimits?.max_amount_per_payment && Number(amount) > Number(liveLimits.max_amount_per_payment) + 0.005);
  const overCount = !!(liveLimits?.max_payments_per_period && String(liveLimits.period || "week") === "week" && (frequency === "daily" ? 5 : 1) > Number(liveLimits.max_payments_per_period));
  const coverage = status?.cycle_coverage;
  const missingDays = frequency === "daily" && coverage?.required === "daily" ? (coverage.missing_weekdays || []) : [];
  const partialCycle = frequency === "daily" && (status?.subscriptions || []).some((s) => ["active", "pending_customer_approval"].includes(s.status)) && missingDays.length > 0 && missingDays.length < 5;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dd-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-blue-600" />
            Direct Debit Wizard — {sfa}
          </DialogTitle>
          <DialogDescription>
            Invite the customer to authorise a GoCardless {scheme === "becs" ? "BECS Direct Debit" : "PayTo"} mandate,
            then create the repayment cycle on it.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — Mandate invite */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-sm flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center ${mandateId ? "bg-emerald-600 text-white" : "bg-blue-600 text-white"}`}>1</span>
              Mandate invite
            </p>
            {mandateId && <Badge className="bg-emerald-100 text-emerald-800">Authorised ✓</Badge>}
          </div>

          {mandateId ? (
            <p className="text-sm text-emerald-700" data-testid="dd-mandate-done">
              Mandate <span className="font-mono">{mandateId}</span> is authorised — go straight to the payment cycle below.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">First name</Label>
                  <Input value={givenName} onChange={(e) => setGivenName(e.target.value)} data-testid="dd-given-name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Last name</Label>
                  <Input value={familyName} onChange={(e) => setFamilyName(e.target.value)} data-testid="dd-family-name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="dd-email" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Mobile (04xx…)</Label>
                  <Input value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="dd-mobile" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Scheme</Label>
                  <Select value={scheme} onValueChange={setScheme}>
                    <SelectTrigger data-testid="dd-scheme-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pay_to">PayTo (standard — weekly plans)</SelectItem>
                      <SelectItem value="becs">BECS Direct Debit (daily plans / PayTo fallback)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {invite.bank?.bsb && (
                  <div className="flex items-end">
                    <p className="text-xs text-slate-500">
                      Bank details prefill: {invite.bank.account_name} • BSB ••{String(invite.bank.bsb).slice(-2)}
                    </p>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-slate-500" data-testid="dd-policy-note">
                Policy: PayTo is standard for weekly collections (≈80% of AU banks). Use Direct Debit for daily plans
                or when the customer's bank doesn't support PayTo.
              </p>
              {scheme === "pay_to" && plan.frequency === "daily" && (
                <p className="text-[11px] text-amber-700" data-testid="dd-payto-daily-warn">
                  ⚠ This deal's plan is DAILY — policy says use BECS Direct Debit for daily collections.
                </p>
              )}
              {scheme === "pay_to" && (
                <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900 space-y-0.5" data-testid="dd-payto-cap-preview">
                  <p className="font-semibold">PayTo agreement limits the customer will authorise</p>
                  {capPreview ? (
                    <>
                      <p>Up to <span className="font-mono font-semibold">A${Number(capPreview.max_amount_per_payment).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> per payment · up to <span className="font-semibold">{capPreview.max_payments_per_week}</span> payments a week.</p>
                      <p className="text-sky-800/80">{capPreview.explain}{plan.fee_per_payment ? ` — the instalment includes A$${Number(plan.fee_per_payment).toFixed(2)}/payment of admin fee, exactly what LoanDisk collects.` : ""}</p>
                      <p className="text-sky-800/80">These can't be raised later without the customer re-authorising, so they leave room for a daily catch-up plan on top of the loan payments.</p>
                      <p className="text-sky-800/80">Instalment source: <span className="font-mono">{{ loandisk: "LoanDisk loan (what is actually collected)", ftla: "signed FTLA terms", contract_wizard: "contract wizard fees + Monday offer", monday_offer: "Monday offer", application: "application" }[plan.source] || plan.source || "—"}</span></p>
                      {!plan.fee_per_payment && plan.source !== "loandisk" && plan.source !== "ftla" && (
                        <p className="text-amber-800 font-medium" data-testid="dd-payto-no-fee-warn">⚠ No admin fee found for this deal (Monday "Fees" is 0 and no FTLA/LoanDisk loan yet). LoanDisk spreads the admin fee over the instalments — if this loan carries one, the real instalment is higher than A${Number(plan.amount_per_payment || 0).toFixed(2)}. Generate the FTLA first (its fees feed the cap) or correct the instalment here:</p>
                      )}
                      {!plan.fee_per_payment && plan.source !== "loandisk" && plan.source !== "ftla" && (
                        <div className="flex items-center gap-2">
                          <Label className="text-[11px] text-sky-900">Instalment incl. fees (A$)</Label>
                          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-7 w-32 text-xs bg-white" data-testid="dd-payto-instalment-override" />
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-amber-700">No instalment amount on the offer — enter the amount per payment below first so the cap is set from it (fallback is A$10,000).</p>
                  )}
                </div>
              )}

              {!inviteResult ? (
                <Button onClick={handleCreateInvite} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dd-create-invite-btn">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Mandate Invite
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-slate-600">
                    Billing request <span className="font-mono">{inviteResult.billing_request_id}</span> — send the authorisation link:
                  </p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <Button size="sm" variant="outline" onClick={handleSendEmail} disabled={sendingEmail || !email} data-testid="dd-send-email-btn">
                      {sendingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Email
                    </Button>
                    <Button size="sm" variant="outline" data-testid="dd-copy-link-btn"
                      onClick={() => { navigator.clipboard.writeText(inviteResult.authorisation_url); toast.success("Authorisation link copied"); }}>
                      <Copy className="w-4 h-4 mr-1" /> Copy link
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">Once the customer authorises, hit Refresh on the panel — the mandate appears here automatically. SMS is off for now — email the link or copy it into your own message.</p>
                  {scheme !== "becs" && (
                    <Button size="sm" variant="ghost" onClick={handleReissueAsDD} disabled={creating}
                      className="text-slate-500 hover:text-slate-700" data-testid="dd-reissue-becs-btn">
                      {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      Bank refused PayTo? Re-issue as Direct Debit
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Step 2 — Payment cycle */}
        <div className={`border rounded-lg p-4 space-y-3 ${!mandateId ? "opacity-60" : ""}`}>
          <div className="flex items-center justify-between">
            <p className="font-semibold text-sm flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] flex items-center justify-center">2</span>
              Payment cycle
            </p>
            {plan.source && (
              <span className="text-[11px] text-slate-400">prefilled from {plan.source === "monday_offer" ? "the contract offer" : "the deal"}</span>
            )}
          </div>
          {!mandateId && (
            <p className="text-xs text-amber-700" data-testid="dd-cycle-blocked-note">
              Available once the customer has authorised the mandate.
            </p>
          )}
          {mandateId && !plan.fee_per_payment && plan.source !== "loandisk" && plan.source !== "ftla" && !existingAmt && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="dd-cycle-no-fee-warn">
              ⚠ The prefilled A${Number(plan.amount_per_payment || 0).toFixed(2)} comes from the Monday offer with no admin fee — LoanDisk spreads the admin fee over the instalments, so check the FTLA / LoanDisk repayment and collect that amount.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Amount per payment (A$)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!mandateId} data-testid="dd-amount" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency} disabled={!mandateId}>
                <SelectTrigger data-testid="dd-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily (5 collections/week, Mon–Fri)</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Term (weeks)</Label>
              <Input type="number" value={weeks} onChange={(e) => setWeeks(e.target.value)} disabled={!mandateId} data-testid="dd-weeks" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">First collection date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!mandateId} data-testid="dd-start-date" />
            </div>
          </div>
          {liveLimits && (
            <div className={`p-2 rounded border text-xs ${overCap ? "bg-red-50 border-red-300 text-red-800" : "bg-sky-50 border-sky-200 text-sky-900"}`} data-testid="dd-payto-live-limits">
              <span className="font-semibold">PayTo agreement:</span> {liveLimits.label}.
              {overCap && (
                <span className="block mt-1" data-testid="dd-payto-over-cap">
                  ✗ A${Number(amount).toFixed(2)} is above the agreement cap of A${Number(liveLimits.max_amount_per_payment).toFixed(2)} — the customer's bank will reject every payment.
                  Lower the amount, or re-issue the agreement (customer re-authorises) / switch to BECS Direct Debit.
                </span>
              )}
              {overCount && (
                <span className="block mt-1" data-testid="dd-payto-over-count">
                  ✗ {frequency === "daily" ? 5 : 1} payment{frequency === "daily" ? "s" : ""} a week on an agreement that allows {liveLimits.max_payments_per_period} — re-issue the agreement or use BECS Direct Debit.
                </span>
              )}
            </div>
          )}
          {partialCycle && (
            <div className="p-2 rounded bg-blue-50 border border-blue-200 text-xs text-blue-800" data-testid="dd-missing-days-note">
              Some weekdays are already mapped — this will create only <span className="font-semibold">{missingDays.join(", ")}</span>,
              each starting on its first occurrence on/after your chosen date. Nothing collects before its start date.
            </div>
          )}
          {amount && weeks ? (
            <p className="text-xs text-slate-600" data-testid="dd-cycle-summary">
              {frequency === "daily"
                ? partialCycle
                  ? `Creates ${missingDays.length} weekly subscription${missingDays.length > 1 ? "s" : ""} (${missingDays.join(", ")}) of A$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} × ${weeks} weeks to complete the Mon–Fri cycle`
                  : `Creates 5 weekly subscriptions (Mon–Fri) of A$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} × ${weeks} weeks — ${numPayments} payments, A$${(Number(amount) * numPayments).toLocaleString(undefined, { minimumFractionDigits: 2 })} total`
                : `Creates 1 weekly subscription of A$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} × ${weeks} payments — A$${(Number(amount) * numPayments).toLocaleString(undefined, { minimumFractionDigits: 2 })} total`}
            </p>
          ) : null}
          <Button
            onClick={handleCreateCycle}
            disabled={!mandateId || creatingSubs || !amount || !weeks || !startDate || overCap || overCount}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="dd-create-cycle-btn"
          >
            {creatingSubs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {partialCycle ? `Create Missing Days (${missingDays.join(", ")})` : "Create Payment Cycle in GoCardless"}
          </Button>
          {subsResult?.created?.length > 0 && (
            <div className="text-xs space-y-1" data-testid="dd-subs-created">
              {subsResult.created.map((s) => (
                <p key={s.id} className="text-emerald-700">✓ {s.name} — starts {s.start_date}</p>
              ))}
              {subsResult.errors?.map((e, i) => (
                <p key={i} className="text-red-600">✗ {e.start_date}: {e.error}</p>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
